import * as d3 from 'd3';
import { getCardBg, getCardBorder, getAvatarBg, getAvatarTextColor, getInitials } from './treeHelpers';
import { DEMO_PEDIGREE } from '../data/demoPedigree';
import { getTranslatedTerm } from '../data/kinshipTranslations';

// ─── Constants ──────────────────────────────────────────────────────
export const CARD_W = 220;
export const CARD_H = 92;
export const GAP_X = 30;
export const GAP_COUPLE = 40;
export const LEVEL_GAP = 120;

// ─── Draw a single person card ──────────────────────────────────────
export function drawPersonCard(g, person, x, y, { isActive, query, selectedLanguage, activePersonId, onViewFamily, onNodeClick, tooltipEntries } = {}) {
  const cardG = g.append('g')
    .attr('class', 'node')
    .attr('transform', `translate(${x}, ${y})`)
    .style('cursor', 'pointer');

  // Determine highlight style
  const isActiveRoot = isActive ? isActive(person) : false;

  // Card background
  cardG.append('rect')
    .attr('x', -CARD_W / 2).attr('y', -CARD_H / 2)
    .attr('width', CARD_W).attr('height', CARD_H)
    .attr('rx', 14)
    .attr('fill', getCardBg(person))
    .attr('stroke', isActiveRoot ? '#4f46e5' : getCardBorder(person))
    .attr('stroke-width', isActiveRoot ? 3 : 2)
    .attr('filter', isActiveRoot ? 'drop-shadow(0 0 8px rgba(79,70,229,0.4))' : 'drop-shadow(0 1px 3px rgba(0,0,0,0.08))');

  // Search highlight ring
  if (query && person.name && person.name.toLowerCase().includes(query)) {
    cardG.append('rect')
      .attr('x', -CARD_W / 2 - 6).attr('y', -CARD_H / 2 - 6)
      .attr('width', CARD_W + 12).attr('height', CARD_H + 12)
      .attr('rx', 18).attr('fill', 'none')
      .attr('stroke', '#6366f1').attr('stroke-width', 3)
      .attr('stroke-dasharray', '6 3');
  }

  // Active root indicator badge — only show "YOU" on your own person
  if (isActiveRoot && activePersonId === DEMO_PEDIGREE.root.id) {
    cardG.append('rect')
      .attr('x', -CARD_W / 2).attr('y', -CARD_H / 2 - 16)
      .attr('width', 38).attr('height', 16)
      .attr('rx', 4)
      .attr('fill', '#4f46e5');
    cardG.append('text')
      .attr('x', -CARD_W / 2 + 19).attr('y', -CARD_H / 2 - 5)
      .attr('text-anchor', 'middle')
      .attr('fill', '#ffffff').attr('font-size', '9px').attr('font-weight', '700')
      .attr('style', 'pointer-events: none')
      .text('YOU');
  }

  // Avatar circle
  cardG.append('circle')
    .attr('cx', -CARD_W / 2 + 35).attr('cy', -8).attr('r', 22)
    .attr('fill', getAvatarBg(person))
    .attr('stroke', getCardBorder(person)).attr('stroke-width', 1.5);

  // Avatar initials
  cardG.append('text')
    .attr('x', -CARD_W / 2 + 35).attr('y', -2)
    .attr('text-anchor', 'middle')
    .attr('fill', getAvatarTextColor(person))
    .attr('font-size', '13px').attr('font-weight', '700')
    .attr('style', 'pointer-events: none')
    .text(getInitials(person.name));

  // Name
  cardG.append('text')
    .attr('x', -CARD_W / 2 + 65).attr('y', -18)
    .attr('fill', '#1f2937').attr('font-size', '13px').attr('font-weight', '600')
    .attr('style', 'pointer-events: none')
    .text(person.name.length > 16 ? person.name.slice(0, 15) + '…' : person.name || '');

  // Status
  cardG.append('text')
    .attr('x', -CARD_W / 2 + 65).attr('y', 0)
    .attr('fill', '#9ca3af').attr('font-size', '11px')
    .attr('style', 'pointer-events: none')
    .text(person.isLiving !== false ? (person.gender === 'MALE' ? 'Male' : person.gender === 'FEMALE' ? 'Female' : '') : 'Deceased');

  // Kinship term (translated)
  const displayTerm = getTranslatedTerm(person.kinshipTerm, selectedLanguage);
  if (person.kinshipTerm) {
    cardG.append('text')
      .attr('x', -CARD_W / 2 + 65).attr('y', 16)
      .attr('fill', '#6366f1').attr('font-size', '11px').attr('font-weight', '500')
      .attr('style', 'pointer-events: none')
      .text(displayTerm);
  }

  // ── "View Family" indicator (for persons with family data) ──
  if (person.family) {
    const vfG = cardG.append('g')
      .attr('class', 'view-family-btn')
      .attr('transform', `translate(${CARD_W / 2 - 18}, ${-CARD_H / 2 + 14})`)
      .attr('opacity', 0)
      .style('pointer-events', 'all');

    vfG.append('circle')
      .attr('r', 10)
      .attr('fill', '#6366f1')
      .attr('stroke', '#fff')
      .attr('stroke-width', 1.5);

    vfG.append('text')
      .attr('x', 0).attr('y', 1)
      .attr('text-anchor', 'middle')
      .attr('fill', '#fff')
      .attr('font-size', '11px')
      .attr('font-weight', '700')
      .attr('style', 'pointer-events: none')
      .text('→');

    // Store for hover show/hide
    cardG.__viewFamilyBtn = vfG;
  }

  // ── Hover highlight rect ──
  const highlightRect = cardG.append('rect')
    .attr('x', -CARD_W / 2 - 2).attr('y', -CARD_H / 2 - 2)
    .attr('width', CARD_W + 4).attr('height', CARD_H + 4)
    .attr('rx', 16)
    .attr('fill', 'none')
    .attr('stroke', getCardBorder(person))
    .attr('stroke-width', 0)
    .attr('opacity', 0);

  if (tooltipEntries) {
    tooltipEntries.push({ person, x, y, highlightRect });
  }

  // ── Hover event handlers ──
  cardG.on('mouseenter', function(event) {
    d3.select(this).transition().duration(150)
      .attr('transform', `translate(${x}, ${y}) scale(1.03)`);
    highlightRect.transition().duration(150)
      .attr('stroke-width', 2.5)
      .attr('opacity', 1)
      .attr('stroke', '#6366f1');
    d3.select(this.parentNode).select(`.tooltip-${person.id}`).transition().duration(150)
      .attr('opacity', 1);
    // Show "View Family" button
    if (cardG.__viewFamilyBtn) {
      cardG.__viewFamilyBtn.transition().duration(150).attr('opacity', 1);
    }
  })
  .on('mouseleave', function(event) {
    d3.select(this).transition().duration(150)
      .attr('transform', `translate(${x}, ${y}) scale(1)`);
    highlightRect.transition().duration(150)
      .attr('stroke-width', 0)
      .attr('opacity', 0);
    d3.select(this.parentNode).select(`.tooltip-${person.id}`).transition().duration(150)
      .attr('opacity', 0);
    // Hide "View Family" button
    if (cardG.__viewFamilyBtn) {
      cardG.__viewFamilyBtn.transition().duration(150).attr('opacity', 0);
    }
  })
  .on('click', (event) => {
    event.stopPropagation();
    if (person.family && onViewFamily) {
      onViewFamily(person);
    } else if (onNodeClick) {
      onNodeClick(person);
    }
  });

  return cardG;
}

// ─── Draw a couple connector (heart between two cards) ──────────────
export function drawCoupleConnector(g, leftX, rightX, y) {
  const midX = (leftX + rightX) / 2;

  g.append('line')
    .attr('x1', leftX + CARD_W / 2).attr('y1', y)
    .attr('x2', midX - 12).attr('y2', y)
    .attr('stroke', '#d1d5db').attr('stroke-width', 2);

  g.append('text')
    .attr('x', midX).attr('y', y + 5)
    .attr('text-anchor', 'middle')
    .attr('fill', '#ef4444').attr('font-size', '16px')
    .text('❤');

  g.append('line')
    .attr('x1', midX + 12).attr('y1', y)
    .attr('x2', rightX - CARD_W / 2).attr('y2', y)
    .attr('stroke', '#d1d5db').attr('stroke-width', 2);
}